import moment from 'moment';

import callApi from '../../util/apiCaller.js';
import { slugify } from '../../util/text.js';

import { getTranslation } from '../Intl/IntlHelper.js';
import { addError } from '../Error/ErrorActions.js';
import { formatPrice } from '../../util/price.js';
import { convertLineAmount, getCatalogCurrencyPrice, isZeroDecimalCurrency } from '../Currency/currencyPrices.js';

// Export Constants
export const SET_PRODUCTS = 'SET_PRODUCTS';

// Export Actions

export function getProductsRequest(isAll = false) {
    return dispatch => {
        return callApi(`products${isAll ? '/all' : ''}`).then(res => {
            if(res.products) {
                dispatch(setProducts(res.products));
            }
            return res.products;
        }).catch(error => {
            dispatch(addError(error));
            return null;
        });
    };
}

export function editProductRequest(product, fetchAll = true) {
    return dispatch => {
        return callApi('product/edit', 'post', { product }).then(res => {
            if(res.product) {
                fetchAll && dispatch(getProductsRequest(true));
            }
            return res.product;
        }).catch(error => {
            dispatch(addError(error));
            return null;
        });
    };
}

export function getFilteredProducts(products, filters = { isActive: null, isVariation: null }) {
    // console.log('getFilteredProducts', filters);
    Object.keys(filters || {}).forEach(key => {
        if(filters[key] !== null) {
            products = products.filter(product => {
                // product[key] !== 'undefined' && product[key] !== filters[key] && console.log('Prorduct::: get from store::: Exclude', product, key, filters[key]);
                return typeof product[key] === 'undefined' || product[key] === filters[key];
            });
        }
    });
    // console.log('products filtered', products);
    return products;
}

export function getProducts(store, filters = {}) {
    return getFilteredProducts(store.products.data, filters);
}

export function getProduct(store, productId) {
    return store.products.data.find(product => product._id === productId);
}

export function getProductBySlug(store, productSlug, lang = null) {
    return store.products.data.find(product => {
        return getProductSlug(product, lang || store.intl.locale) === (productSlug && productSlug.toLowerCase());
    });
}

export function getProductSlug(product, lang) {
    return (getTranslation(product, 'slug', lang) && getTranslation(product, 'slug', lang).toLowerCase()) || slugify(getTranslation(product, 'name', lang)).toLowerCase();
}

export function getProductsAttributeList(store, attributeName, lang = null) {
    let attributes = [];
    getProducts(store).forEach(product => {
        const productAttributes = getProductAttributeList(product, attributeName, lang || store.intl.locale);
        if(productAttributes) {
            attributes = attributes.concat(productAttributes);
        }
    });
    return attributes.filter((attribute, index, self) => self.indexOf(attribute) === index) || [];
}

export function getProductAttributeList(product, attributeName, lang) {
    return getTranslation(product, attributeName, lang) || [];
}

// Pourcentage du prix d'un tome pour chaque livre au-delà du premier exemplaire.
// Vide sur un livre papier : 50 %. « 0 » : on revient aux paliers specialPrices.
export function getExtraCopyRate(product) {
	if(!product) {
		return null;
	}
	const isBook = getProductOption(product, 'useForConversationBook') === true
		|| getProductOption(product, 'useForConversationBook') === 'true';
	const raw = getProductOption(product, 'extraCopyRate');
	const text = raw == null ? '' : String(raw).trim().toLowerCase();
	if(!text) {
		return isBook ? 50 : null;
	}
	if(text === '0' || text === '0%' || text === 'off') {
		return null;
	}
	const value = parseFloat(text.replace('%', ''));
	if(!Number.isFinite(value) || value <= 0 || value > 100) {
		return null;
	}
	return value;
}

// Moitié commerciale : 39,99 € → 19,99 €, pas 20,00 €. Pareil dans chaque devise.
export function priceExtraUnit(unit, rate, currency = 'EUR') {
	const value = parseFloat(unit) || 0;
	if(!(value > 0) || !(rate > 0)) {
		return 0;
	}
	if(isZeroDecimalCurrency(currency)) {
		return Math.floor((value * rate) / 100);
	}
	const cents = Math.round(value * 100);
	return Math.floor((cents * rate) / 100) / 100;
}

function getCopyPricingBasis(product) {
	const catalogUnit = parseFloat(product.catalogBaseTTC);
	const line = parseFloat(product.priceTTC);
	const base = parseFloat(product.basePriceTTC);
	const unit = catalogUnit > 0 ? catalogUnit : (line > 0 ? line : 0);
	let volumes = 1;
	if(unit > 0 && base > 0) {
		const count = Math.round(base / unit);
		if(count >= 1) {
			volumes = count;
		}
	}
	return { unit, volumes };
}

function getProductPriceFromSpecials(product, quantity = 1, inclTax = true, referral = null) {
	let price = 0;
	const specialPrice = getProductSpecialPrice(product, 'priceTTC', quantity, referral);
	if(product) {
		price = parseFloat(specialPrice ? getProductSpecialPriceValue(product, specialPrice, quantity || 1) : (product.priceTTC * (quantity || 1)));
	}
	if(!inclTax && product) {
		price /= (1 + parseFloat(product.taxRate));
	}
	return price;
}

// Premier exemplaire au prix de la ligne (remise multi-tomes comprise).
// Chaque livre des exemplaires suivants : rate % du prix d'un tome, au centime inférieur.
export function getExtraCopyBreakdown(product, quantity = 1, referral = null) {
	const qty = parseInt(quantity, 10) || 1;
	const rate = getExtraCopyRate(product);
	if(!product || !rate || qty <= 1) {
		return null;
	}
	const { unit, volumes } = getCopyPricingBasis(product);
	if(!(unit > 0) || !(volumes >= 1)) {
		return null;
	}
	return {
		rate,
		unit,
		volumes,
		firstEur: getProductPriceFromSpecials(product, 1, true, referral),
		extraBooks: (qty - 1) * volumes,
		extraUnitEur: priceExtraUnit(unit, rate, 'EUR'),
	};
}

export function getProductPrice(product, quantity = 1, inclTax = true, referral = null) {
	const qty = parseInt(quantity, 10) || 1;
	const breakdown = product ? getExtraCopyBreakdown(product, qty, referral) : null;
	if(breakdown) {
		const cents = Math.round(breakdown.firstEur * 100) + (breakdown.extraBooks * Math.round(breakdown.extraUnitEur * 100));
		let price = cents / 100;
		if(!inclTax && product) {
			price /= (1 + parseFloat(product.taxRate));
		}
		return price;
	}
	return getProductPriceFromSpecials(product, quantity, inclTax, referral);
}

// Ligne payée : le 1er exemplaire suit le catalogue, chaque livre en plus est
// la moitié (ou le taux du produit) du prix du tome dans la devise.
export function buildPaidPriceLine(item, quantity, currency, fxRate, referral = null) {
	const qty = parseInt(quantity, 10) || parseInt(item?.quantity, 10) || 1;
	const eurAmount = getProductPrice(item, qty, true, referral);
	const catalogBase = item?.catalogBaseTTC != null && `${item.catalogBaseTTC}` !== '' ? item.catalogBaseTTC : item?.priceTTC;
	const breakdown = getExtraCopyBreakdown(item, qty, referral);
	const code = `${currency || 'EUR'}`.toUpperCase();
	const line = {
		eurAmount,
		currencyPrices: item?.currencyPrices,
		catalogBase,
		useCatalog: !getProductSpecialPrice(item, 'priceTTC', breakdown ? 1 : qty, referral),
	};
	if(breakdown && code !== 'EUR') {
		const catalogUnit = getCatalogCurrencyPrice(item, code);
		const unitInCurrency = catalogUnit != null ? catalogUnit : convertLineAmount({
			eurAmount: breakdown.unit,
			currency: code,
			rate: fxRate,
			currencyPrices: item.currencyPrices,
			catalogBase: breakdown.unit,
			useCatalog: false,
		});
		line.extraCopies = {
			firstEur: breakdown.firstEur,
			count: breakdown.extraBooks,
			unitInCurrency: priceExtraUnit(unitInCurrency, breakdown.rate, code),
		};
	}
	return line;
}

export function getProductSpecialPrices(product, key = 'priceTTC', quantity = null, referral = null) {
	return ((product || {}).specialPrices || []).filter(specialPrice => filterProductSpecialPrice(specialPrice, key, quantity, referral));
}

export function getProductSpecialPrice(product, key = 'priceTTC', quantity = null, referral = null) {
    // console.log('getProductSpecialPrice', (product || {}).specialPrices, key, referral);
    return ((product || {}).specialPrices || []).sort((spA, spB) => (spA.quantity || 0) - (spB.quantity || 0)).find(specialPrice => filterProductSpecialPrice(specialPrice, key, quantity, referral)) || (referral && getProductSpecialPrice(product, key, quantity)) || null;
}

export function filterProductSpecialPrice(specialPrice, key, quantity = null, referral = null) {
	// console.log(specialPrice);
	// console.log('key', specialPrice.key === key);
	// console.log('quantity', !specialPrice.quantity, parseInt(quantity || 1, 10) <= 1, parseInt(quantity, 10), parseInt(specialPrice.quantity || 0, 10));
	// console.log('referral', (!specialPrice.referral || specialPrice.referral === referral));
	// console.log('dateThreshold', (!specialPrice.dateThreshold || moment.utc().isSameOrBefore(getProductSpecialPriceDateThreshold(specialPrice))));
	return (
		specialPrice.key === key
		&& (!specialPrice.referral || specialPrice.referral === referral)
		&& (!specialPrice.dateThreshold || moment.utc().isSameOrBefore(getProductSpecialPriceDateThreshold(specialPrice)))
		&& (quantity === null || !specialPrice.quantity || parseInt(quantity, 10) >= parseInt(specialPrice.quantity || 0, 10))
	);
}

export function getProductSpecialPriceValue(product, specialPrice, quantity = 1) {
	const basePrice = parseFloat(product.priceTTC) * quantity;
    return specialPrice.value.includes('%') ? ((parseFloat(specialPrice.value) * basePrice) / 100) : specialPrice.value;
}

export function getProductSpecialPriceDateThreshold(specialPrice) {
	if(specialPrice?.dateThreshold) {
		let date = moment.utc(specialPrice.dateThreshold);

		// Add X days to now
		if(specialPrice.dateThreshold.includes('+')) {
			// console.log(`date threshold +${parseInt(specialPrice.dateThreshold, 10)} Jours`);
			date = moment.utc().add(parseInt(specialPrice.dateThreshold, 10), 'days');

		// Set next date to next day of month (for value of 8 => date is next 8th of the month)
		} else if(!specialPrice.dateThreshold.includes('/')) {
			let diffDays = parseInt(specialPrice.dateThreshold - moment.utc().format('D'), 10);
			if(diffDays < 0) {
				diffDays = 31 + diffDays;
			}
			date = moment.utc().add(diffDays, 'days');
		}
		// console.log('date threshold', specialPrice.dateThreshold, dateFormat(date, 'LL'));
		return date.endOf('day');
	}
	return null;
}

export function getProductBooksDiscountRate(product, books = [], referral = '') {
	let discountPercent = null;
	(getProductOption(product, 'bookPrices') || []).forEach(bookPrice => {
		if(books.length >= bookPrice.count) {
			discountPercent = 100
				- (
					bookPrice.value.includes('%')
					? parseFloat(bookPrice.value)
					: (100
						- (
							(parseFloat(bookPrice.value) * 100)
							/ getProductPrice(product, 1, true, referral)
						)
					)
				);
		}
	});
	return discountPercent;
	// if(this.props.bookDiscounts) {
	//     const booksCount = this.getSelectedBooks('book').length;
	//     const rules = this.props.bookDiscounts.split('/').map(ruleString => {
	//         const [min, rate] = ruleString.split(':');
	//         return {
	//             min,
	//             rate,
	//         };
	//     }).sort((ruleA, ruleB) => ruleB.min - ruleA.min);

	//     const { min, rate } = rules.find(rule => rule.min <= booksCount) || {};

	//     return rate;
	// }
	// return null;
}

export function getProductOption(product, option) {
    return (product && product.options && product.options[option]) || '';
}

export function setProducts(products) {
    return {
        type: SET_PRODUCTS,
        products,
    };
}
